// services/frontend/src/components/AuthModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getOrCreateDeviceId } from '../utils/auth';

const AuthModal = ({
  isOpen,
  defaultMode = 'login',
  onClose,
  onSuccess,
  infoMessage = '',
  initialEmail = '',
  loginPayload = null,
  signupPath = '/signup/step1',
}) => {
  // [회원가입 UX 변경]
  // - 기존: 모달 내 로그인/회원가입 탭
  // - 변경: 로그인만 제공 + 하단에 '회원가입하기' 버튼으로 3-step 회원가입 페이지로 이동
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail || '');
      setPassword('');
      setError('');
    }
  }, [isOpen, defaultMode, initialEmail]);

  if (!isOpen) return null;

  // [중요] 운영에서 REACT_APP_API_URL 미주입 시 localhost로 붙는 문제를 방지합니다.
  //        기본값을 빈 문자열로 두면 `/api/...`가 현재 도메인으로 호출됩니다.
  const apiUrl = (process.env.REACT_APP_API_URL ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '') : '');

  const parseAxiosError = (e) => {
    // 네트워크 레벨 오류 (서버에 도달하지 못함, CORS 등)
    if (e && e.message === 'Network Error' && !e.response) {
      return '네트워크 연결에 문제가 있습니다. 잠시 후 다시 시도해 주세요.';
    }
    const status = e?.response?.status;
    const data = e?.response?.data;

    // 백엔드가 detail/message를 내려주는 경우 우선 사용
    const backendDetail = (data && (data.detail || data.message)) || '';

    if (status === 401 || status === 403) {
      return backendDetail || '이메일 또는 비밀번호가 올바르지 않습니다.';
    }
    if (status === 409) {
      return backendDetail || '이미 가입된 이메일입니다. 로그인으로 진행해 주세요.';
    }
    if (status === 422) {
      // FastAPI의 validation error 형태를 친절히 변환
      const errs = Array.isArray(data?.detail) ? data.detail : [];
      if (errs.length > 0) {
        const msg = errs.map((it) => it?.msg || '').filter(Boolean).join(', ');
        return msg || '입력값을 확인해 주세요.';
      }
      return backendDetail || '입력값을 확인해 주세요.';
    }
    if (status && status >= 500) {
      return backendDetail || '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
    }
    // 기타 상태 코드 일반 처리
    return backendDetail || e?.message || '인증 중 오류가 발생했습니다.';
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoint = '/api/v1/auth/login';
      const deviceId = getOrCreateDeviceId();
      
      const { data } = await axios.post(
        `${apiUrl}${endpoint}`,
        { email, password, ...(loginPayload || {}) },
        {
          headers: {
            'X-Device-Id': deviceId,
          },
        }
      );
      
      if (data?.access_token) {
        const shouldClose = onSuccess?.(data);
        if (shouldClose !== false) {
          onClose?.();
        }
      } else {
        setError('응답에서 토큰을 찾지 못했습니다.');
      }
    } catch (e) {
      setError(parseAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSignup = () => {
    // [회원가입 UX 변경]
    // - 모달을 닫고, 3-step 회원가입 페이지로 이동
    try {
      onClose?.();
    } finally {
      navigate(signupPath);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ width: 380, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 12px 24px rgba(0,0,0,0.12)' }}>
        <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>로그인</strong>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 12 }}>
          <form onSubmit={(e) => { e.preventDefault(); if (!loading) submit(); }} autoComplete="on">
            <div style={{ display: 'grid', gap: 8 }}>
              <input
                type="email"
                placeholder="이메일"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ height: 38, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 16 }}
                disabled={loading}
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ height: 38, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 16 }}
                disabled={loading}
                autoComplete="current-password"
              />
              {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
              <button type="submit" disabled={loading || !email || !password} style={{ height: 38, borderRadius: 8, border: '1px solid #6366f1', background: 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)', color: '#1e1b4b', cursor: 'pointer', fontSize: 16 }}>
                {loading ? '처리 중...' : '로그인'}
              </button>

              {/* 회원가입 안내 및 이동 버튼 */}
              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                <div
                  style={{
                    // [모바일 UX 정렬] 회원가입 유도 문구 가독성 향상
                    color: '#4b5563',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  아직 계정이 없으신가요?
                </div>
                <button
                  type="button"
                  onClick={handleGoToSignup}
                  disabled={loading}
                  style={{
                    // [모바일 UX 정렬] 가입 유도 버튼을 실명인증/주소검색 버튼과 동일 계열 테두리/굵기로 통일
                    height: 36,
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #c9d8ff',
                    background: '#f7f9ff',
                    color: '#1f3d7a',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    width: '100%',
                  }}
                >
                  회원가입하기
                </button>
              </div>

              {infoMessage && (
                <div style={{ color: '#2563eb', fontSize: 13, marginTop: 8 }}>
                  {infoMessage}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
